import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  type Theme,
} from "@mui/material";
import {
  CheckBox as CheckedIcon,
  CheckBoxOutlineBlank as UncheckedIcon,
  CheckCircleOutline as ReadyIcon,
  Download as DownloadIcon,
  ErrorOutline as PendingIcon,
  RadioButtonChecked as SelectedIcon,
  RadioButtonUnchecked as UnselectedIcon,
} from "@mui/icons-material";
import DayRangePicker, { formatRangeLabel } from "../components/DayRangePicker";
import { useTranslation } from "react-i18next";
import { useLocaleFormat } from "../i18n/useLocaleFormat";
import { useAuth } from "../contexts/AuthContext";
import { getExportSchema, runExport } from "./exportApi";
import ExportFiltersSection, {
  allLabel,
  useExportFilterOptions,
} from "./ExportFiltersSection";
import {
  allColumns,
  columnGroups,
  defaultColumns,
  exceedsPdfLimit,
  initialExportSort,
  pdfColumns,
  sortableColumns,
  type ExportColumnGroup,
} from "./exportPrefs";
import { useExportPrefs } from "./useExportPrefs";
import {
  type ExportConfig,
  type ExportFormat,
  type ExportGranularity,
  type ExportPrefs,
  type ExportSchema,
  type ExportSectionChoice,
  type ExportSort,
  type SortDirection,
} from "./types";

type Temporality = "total" | ExportGranularity;

/** Must match the wording of pdfTooLargeMessage in app-backend/src/export/format.ts. */
const PDF_TOO_LARGE_MARKER = "demasiado grande para PDF";

/** Optional sections (e.g. the mention detail) start switched off for longer periods. */
const OPTIONAL_TABLES_MAX_DAYS = 31;

/** Days in a YYYY-MM-DD range, both ends included; 0 when either end is missing. */
function periodDays(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 0;
  const ms =
    Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.round(ms / 86_400_000) + 1 : 0;
}

const TEMPORALITY_LABELS: Record<Temporality, string> = {
  total: "Total",
  month: "Mensual",
  week: "Semanal",
};

const SORT_DIRECTION_LABELS: Record<SortDirection, string> = {
  desc: "Mayor a menor",
  asc: "Menor a mayor",
};

const SANS = (theme: Theme) => theme.typography.fontFamily;

const ACTION_BUTTON_SX = {
  textTransform: "none",
  height: 28,
  py: 0,
  px: 1.25,
  lineHeight: 1,
  color: "text.primary",
  borderColor: "divider",
} as const;

function describeNames(names: string[]): string {
  const shown = names.slice(0, 3).join(", ");
  return names.length > 3 ? `${shown} y ${names.length - 3} más` : shown;
}

function schemaLoadErrorMessage(error: unknown): string {
  const data =
    error && typeof error === "object" && "response" in error
      ? (error as { response?: { data?: { error?: unknown } } }).response?.data
      : undefined;
  if (data && typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return "No se pudo cargar la configuración de exportación.";
}

type Alternative = NonNullable<ExportConfig["alternatives"]>[number];

type Props = {
  config: ExportConfig;
  onExported: () => void;
  onClose: () => void;
};

const ExportDialog: React.FC<Props> = ({ config, onExported, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [reportType, setReportType] = useState(config.reportType);
  const [period, setPeriod] = useState(() => ({
    startDate: config.params["startDate"],
    endDate: config.params["endDate"],
  }));
  const activeConfig = useMemo<ExportConfig>(() => {
    const params = { ...config.params, ...period };
    if (reportType === config.reportType) return { ...config, params };
    const { contextLabels: _labels, ...rest } = config;
    return { ...rest, reportType, params };
  }, [config, reportType, period]);
  const hasPeriod = !!(period.startDate && period.endDate);
  const schemaQuery = useQuery({
    // Bump key when column catalog changes so in-memory React Query cache cannot keep a stale schema.
    queryKey: ["export-schema", "v2", reportType, config.params["via"]],
    queryFn: () => getExportSchema(reportType, config.params["via"]),
    staleTime: 0,
    refetchOnMount: "always",
    retry: 1,
  });
  const schema =
    schemaQuery.data?.type === reportType ? schemaQuery.data : undefined;
  const schemaFailed =
    !schema &&
    !schemaQuery.isPending &&
    !schemaQuery.isFetching &&
    (schemaQuery.isError || schemaQuery.isFetched);

  const alternatives = config.alternatives ?? [];
  const sectionChoices = config.sectionChoices ?? [];
  const [selectedSections, setSelectedSections] = useState<string[]>(() =>
    sectionChoices.length
      ? (config.initialSections ?? sectionChoices.map((c) => c.key))
      : [],
  );

  const reportStep =
    sectionChoices.length > 0 ? (
      <SectionStep
        choices={sectionChoices}
        selected={selectedSections}
        onChange={setSelectedSections}
        disabled={busy}
      />
    ) : (
      <ReportStep
        alternatives={alternatives}
        reportType={reportType}
        onChange={setReportType}
        schema={schema}
        disabled={busy}
      />
    );

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      slotProps={{ paper: { sx: { height: { md: "79vh" } } } }}
    >
      <DialogTitle
        sx={{ pb: 1.5, fontSize: "1.25rem", fontWeight: 600, fontFamily: SANS }}
      >
        Exportar
      </DialogTitle>
      {schema ? (
        <ExportDialogBody
          key={reportType}
          schema={schema}
          config={activeConfig}
          reportLabel={
            alternatives.find((a) => a.reportType === reportType)?.label ??
            schema.title
          }
          reportStep={reportStep}
          selectedSections={sectionChoices.length > 0 ? selectedSections : undefined}
          onPeriodChange={
            hasPeriod
              ? (startDate: string, endDate: string) =>
                  setPeriod({ startDate, endDate })
              : undefined
          }
          busy={busy}
          setBusy={setBusy}
          onExported={onExported}
          onClose={onClose}
        />
      ) : (
        <>
          <DialogContent dividers>
            <Stack spacing={3}>
              {reportStep}
              {schemaFailed ? (
                <Alert severity="error">
                  {schemaLoadErrorMessage(schemaQuery.error)}
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  <Skeleton variant="rounded" height={48} />
                  <Skeleton variant="rounded" height={160} />
                  <Skeleton variant="rounded" height={80} />
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

const ReportStep: React.FC<{
  alternatives: Alternative[];
  reportType: string;
  onChange: (reportType: string) => void;
  schema: ExportSchema | undefined;
  disabled: boolean;
}> = ({ alternatives, reportType, onChange, schema, disabled }) => {
  const choices: Alternative[] =
    alternatives.length > 1
      ? alternatives
      : [
          {
            reportType,
            label: schema?.title ?? "",
            ...(schema?.summary ? { description: schema.summary } : {}),
          },
        ];
  const selectable = alternatives.length > 1;
  return (
    <Step n={1} title="Reporte">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: `repeat(${choices.length}, 1fr)`,
          },
          gap: 1.5,
        }}
      >
        {choices.map((c) =>
          c.label ? (
            <ChoiceCard
              key={c.reportType}
              selected={c.reportType === reportType}
              onClick={selectable ? () => onChange(c.reportType) : undefined}
              disabled={disabled}
              title={c.label}
              description={c.description}
            />
          ) : (
            <Skeleton key={c.reportType} variant="rounded" height={72} />
          ),
        )}
      </Box>
    </Step>
  );
};

const SectionStep: React.FC<{
  choices: ExportSectionChoice[];
  selected: string[];
  onChange: (keys: string[]) => void;
  disabled: boolean;
}> = ({ choices, selected, onChange, disabled }) => {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const toggle = (key: string) => {
    if (selectedSet.has(key)) {
      if (selected.length <= 1) return;
      onChange(selected.filter((k) => k !== key));
      return;
    }
    onChange([...selected, key]);
  };
  return (
    <Step n={1} title="Formatos">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: `repeat(${choices.length}, 1fr)`,
          },
          gap: 1.5,
        }}
      >
        {choices.map((c) => (
          <ChoiceCard
            key={c.key}
            multi
            selected={selectedSet.has(c.key)}
            onClick={() => toggle(c.key)}
            disabled={disabled}
            title={c.label}
            description={c.description}
          />
        ))}
      </Box>
    </Step>
  );
};

type BodyProps = Props & {
  schema: ExportSchema;
  reportLabel: string;
  reportStep: React.ReactNode;
  /** Multi-select section keys from Formatos cards; drives omitTables. */
  selectedSections?: string[] | undefined;
  /** Set when the report has a period; changes only the exported range, not the page. */
  onPeriodChange: ((startDate: string, endDate: string) => void) | undefined;
  busy: boolean;
  setBusy: (busy: boolean) => void;
};

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

const ExportDialogBody: React.FC<BodyProps> = ({
  schema,
  config,
  reportLabel,
  reportStep,
  selectedSections,
  onPeriodChange,
  busy,
  setBusy,
  onExported,
  onClose,
}) => {
  const { getDateFilterMinStartDate, getDateFilterMaxEndDate } = useAuth();
  const { t } = useTranslation("common");
  const { numberLocale } = useLocaleFormat();
  const { prefs, save } = useExportPrefs(schema);
  const initial = prefs as ExportPrefs;

  // Columns are picked from the Excel set when offered; the PDF takes its share of them.
  const pickFormat: ExportFormat = schema.formats.includes("xlsx")
    ? "xlsx"
    : (schema.formats[0] ?? "xlsx");
  const [selected, setSelectedState] = useState<string[]>(
    () =>
      initial.columnsByFormat[pickFormat] ?? defaultColumns(schema, pickFormat),
  );
  const [busyFormat, setBusyFormat] = useState<ExportFormat | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(
    () =>
      Object.fromEntries(
        Object.entries(config.initialFilters ?? {}).filter(
          ([key, values]) =>
            values.length > 0 &&
            (schema.filters ?? []).some((f) => f.key === key),
        ),
      ),
  );
  const [error, setError] = useState<string | null>(null);
  const [temporality, setTemporality] = useState<Temporality>(
    config.initialGranularity ?? "total",
  );
  const [pdfTooLarge, setPdfTooLarge] = useState<string | null>(null);
  const sortOptions = useMemo(() => sortableColumns(schema), [schema]);
  const [sort, setSort] = useState<ExportSort>(() =>
    initialExportSort(schema, config.initialSort),
  );
  const sortLabel = (key: string) => {
    const col = sortOptions.find((c) => c.key === key);
    return col ? (col.choiceLabel ?? col.label) : key;
  };
  const filters = schema.filters ?? [];
  const filterOptions = useExportFilterOptions(
    config.reportType,
    config.params,
    filters.length > 0,
  ).data;

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const isPdf = pickFormat === "pdf";
  const groups = useMemo(
    () => columnGroups(schema, pickFormat),
    [schema, pickFormat],
  );
  const pdfTrimsColumns =
    !isPdf &&
    schema.formats.includes("pdf") &&
    exceedsPdfLimit(schema, selected);
  const multiTable = groups.length > 1;
  const countIn = (group: ExportColumnGroup) =>
    group.columns.filter((c) => selectedSet.has(c.key)).length;
  const blockedByPdfLimit = (key: string) =>
    isPdf &&
    !selectedSet.has(key) &&
    exceedsPdfLimit(schema, [...selected, key]);
  const longPeriod =
    periodDays(config.params["startDate"], config.params["endDate"]) >
    OPTIONAL_TABLES_MAX_DAYS;
  const sectionChoiceKeys = useMemo(
    () => new Set((config.sectionChoices ?? []).map((c) => c.key)),
    [config.sectionChoices],
  );
  const sectionDriven = sectionChoiceKeys.size > 0 && selectedSections != null;
  const defaultOmitted = useMemo(() => {
    if (sectionDriven) {
      // Format cards control section keys (ondemand/livestreams/clips) and any
      // optional child table whose key starts with that section prefix.
      const omitted = new Set<string>();
      const sectionKeys = Array.from(sectionChoiceKeys);
      for (const key of sectionKeys) {
        if (!selectedSections.includes(key)) omitted.add(key);
      }
      for (const t of schema.tables ?? []) {
        if (!t.optional) continue;
        if (sectionChoiceKeys.has(t.key)) {
          if (!selectedSections.includes(t.key)) omitted.add(t.key);
          continue;
        }
        const parent = sectionKeys.find((s) =>
          t.key.toLowerCase().startsWith(s.toLowerCase()),
        );
        if (parent && !selectedSections.includes(parent)) omitted.add(t.key);
      }
      return Array.from(omitted);
    }
    return (schema.tables ?? [])
      .filter((t) => t.optional && (t.defaultOff || longPeriod))
      .map((t) => t.key);
  }, [
    longPeriod,
    schema.tables,
    sectionChoiceKeys,
    sectionDriven,
    selectedSections,
  ]);
  const [omitted, setOmitted] = useState<string[]>(defaultOmitted);
  useEffect(() => setOmitted(defaultOmitted), [defaultOmitted]);
  useEffect(() => setPdfTooLarge(null), [filterValues, temporality, omitted]);
  const tableInfo = (group: ExportColumnGroup) =>
    schema.tables?.find((t) => t.key === group.key);
  const isOmitted = (group: ExportColumnGroup) =>
    !!group.key && omitted.includes(group.key);
  const emptyGroups = groups.filter((g) => {
    if (isOmitted(g) || countIn(g) > 0) return false;
    if (tableInfo(g)?.optional && g.columns.length === 0) return false;
    return true;
  });

  const requireOneOf = schema.requireOneOf ?? [];
  const requirementMet =
    requireOneOf.length === 0 ||
    requireOneOf.some((key) => (filterValues[key]?.length ?? 0) > 0);
  const requirementText = `Elige ${requireOneOf
    .map(
      (key) => filters.find((f) => f.key === key)?.label.toLowerCase() ?? key,
    )
    .join(" o ")}`;

  const pending: string[] = [];
  if (!requirementMet) pending.push(requirementText);
  if (sectionDriven && (selectedSections?.length ?? 0) === 0) {
    pending.push("Elige al menos un formato");
  }
  if (emptyGroups.length > 0) {
    pending.push(
      multiTable
        ? `Elige columnas en ${emptyGroups.map((g) => g.label).join(" y ")}`
        : "Elige columnas",
    );
  }
  const canSubmit = !busy && pending.length === 0;

  const setSelected = (keys: string[]) => {
    const order = new Map(schema.columns.map((c, i) => [c.key, i]));
    const sorted = [...keys].sort(
      (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0),
    );
    setSelectedState(sorted);
  };

  const toggleColumn = (key: string) => {
    if (selectedSet.has(key)) {
      setSelected(selected.filter((k) => k !== key));
    } else if (!blockedByPdfLimit(key)) {
      setSelected([...selected, key]);
    }
  };

  const splitsByPeriod =
    !!schema.periodSplit &&
    !!config.params["startDate"] &&
    !!config.params["endDate"];

  const handleExport = async (format: ExportFormat) => {
    if (!canSubmit) return;
    setBusy(true);
    setBusyFormat(format);
    setError(null);
    try {
      await runExport(
        config.reportType,
        {
          format,
          columns:
            format === "pdf" && !isPdf
              ? pdfColumns(schema, selected)
              : selected,
          rowScope: "all",
          sort,
          filters: filterValues,
          ...(splitsByPeriod && temporality !== "total"
            ? { granularity: temporality }
            : {}),
          ...(omitted.length ? { omitTables: omitted } : {}),
        },
        config.params,
      );
      save({
        format,
        columnsByFormat: sameList(selected, defaultColumns(schema, pickFormat))
          ? {}
          : { [pickFormat]: selected },
      });
      onExported();
    } catch (e) {
      const message = e instanceof Error ? e.message : "No se pudo exportar.";
      if (format === "pdf" && message.includes(PDF_TOO_LARGE_MARKER)) {
        setPdfTooLarge(message);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
      setBusyFormat(null);
    }
  };

  const startDate = config.params["startDate"];
  const endDate = config.params["endDate"];

  const summaryRows: Array<{ label: string; value: string }> = [
    { label: t("export.report"), value: reportLabel },
    ...(startDate && endDate
      ? [{ label: t("export.period"), value: formatRangeLabel(startDate, endDate, numberLocale) }]
      : []),
    ...(splitsByPeriod
      ? [{ label: "Temporalidad", value: TEMPORALITY_LABELS[temporality] }]
      : []),
    ...filters.map((f) => {
      const picked = filterValues[f.key] ?? [];
      const labels = new Map(
        (filterOptions?.[f.key] ?? []).map((o) => [o.value, o.label]),
      );
      return {
        label: f.label,
        value: picked.length
          ? describeNames(picked.map((v) => labels.get(v) ?? v))
          : allLabel(f.label),
      };
    }),
    ...(config.contextLabels ?? []),
    {
      label: "Orden",
      value: `${sortLabel(sort.field)}, ${SORT_DIRECTION_LABELS[sort.direction].toLowerCase()}`,
    },
    {
      label: multiTable ? "Secciones" : "Columnas",
      value: multiTable
        ? groups
            .map((g) =>
              isOmitted(g)
                ? `${g.label}: no incluida`
                : `${g.label}: ${countIn(g)} columnas`,
            )
            .join(" · ")
        : `${selected.length} de ${groups[0]?.columns.length ?? 0}`,
    },
  ];

  return (
    <>
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
          overflow: { xs: "auto", md: "hidden" },
        }}
      >
        <Box sx={{ p: 3, overflowY: { md: "auto" } }}>
          <Stack spacing={3.5} divider={<Divider flexItem />}>
            {reportStep}

            <Step n={2} title="Datos">
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  columnGap={3}
                  rowGap={2}
                  flexWrap="wrap"
                  alignItems="flex-start"
                >
                  {startDate && endDate && onPeriodChange ? (
                    <Box>
                      <FieldLabel>Período</FieldLabel>
                      <DayRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onChange={onPeriodChange}
                        minDate={getDateFilterMinStartDate()}
                        maxDate={getDateFilterMaxEndDate()}
                        popoverPlacement="right"
                      />
                    </Box>
                  ) : null}
                  {splitsByPeriod ? (
                    <Box>
                      <FieldLabel>Temporalidad</FieldLabel>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={temporality}
                        disabled={busy}
                        onChange={(_e, next: Temporality | null) => {
                          if (next) setTemporality(next);
                        }}
                      >
                        {(Object.keys(TEMPORALITY_LABELS) as Temporality[]).map(
                          (key) => (
                            <ToggleButton
                              key={key}
                              value={key}
                              sx={{ textTransform: "none", px: 1.5, py: 0.5 }}
                            >
                              {TEMPORALITY_LABELS[key]}
                            </ToggleButton>
                          ),
                        )}
                      </ToggleButtonGroup>
                    </Box>
                  ) : null}
                  {sortOptions.length > 1 ? (
                    <Box>
                      <FieldLabel>Ordenar por</FieldLabel>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Select
                          size="small"
                          value={sort.field}
                          disabled={busy}
                          onChange={(e) =>
                            setSort((prev) => ({ ...prev, field: e.target.value }))
                          }
                          sx={{ minWidth: 180 }}
                        >
                          {sortOptions.map((c) => (
                            <MenuItem key={c.key} value={c.key}>
                              {c.choiceLabel ?? c.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={sort.direction}
                          disabled={busy}
                          onChange={(_e, next: SortDirection | null) => {
                            if (next) setSort((prev) => ({ ...prev, direction: next }));
                          }}
                        >
                          {(["desc", "asc"] as SortDirection[]).map((dir) => (
                            <ToggleButton
                              key={dir}
                              value={dir}
                              sx={{ textTransform: "none", px: 1.5, py: 0.5 }}
                            >
                              {SORT_DIRECTION_LABELS[dir]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Stack>
                    </Box>
                  ) : null}
                </Stack>
                {filters.length > 0 ? (
                  <Box>
                    <FieldLabel>Filtros</FieldLabel>
                    <ExportFiltersSection
                      reportType={config.reportType}
                      params={config.params}
                      filters={filters}
                      value={filterValues}
                      onChange={setFilterValues}
                    />
                  </Box>
                ) : null}
              </Stack>
            </Step>

            <Step
              n={3}
              title={multiTable ? "Secciones" : "Columnas"}
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelected(allColumns(schema, pickFormat))}
                    sx={ACTION_BUTTON_SX}
                  >
                    {isPdf ? "Máximo posible" : "Todas"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setSelected(defaultColumns(schema, pickFormat))
                    }
                    sx={ACTION_BUTTON_SX}
                  >
                    Predeterminadas
                  </Button>
                </Stack>
              }
            >
              <Stack spacing={1.5}>
                {groups.map((group) => {
                  const count = countIn(group);
                  const info = tableInfo(group);
                  const off = isOmitted(group);
                  return (
                    <Box key={group.key ?? "all"}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 0.25 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {multiTable ? (
                            <Box
                              component="span"
                              sx={{ color: "text.primary", fontWeight: 600 }}
                            >
                              {group.label}{" "}
                            </Box>
                          ) : null}
                          {off
                            ? "No incluida"
                            : group.columns.length === 0
                              ? "Incluida"
                              : `${count}/${
                                  isPdf
                                    ? Math.min(
                                        schema.maxPdfColumns,
                                        group.columns.length,
                                      )
                                    : group.columns.length
                                }`}
                        </Typography>
                        {info?.optional &&
                        group.key &&
                        !sectionChoiceKeys.has(group.key) ? (
                          <FormControlLabel
                            sx={{ ml: "auto", mr: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={!off}
                                onChange={(_, on) =>
                                  setOmitted((prev) =>
                                    on
                                      ? prev.filter((k) => k !== group.key)
                                      : [...prev, group.key!],
                                  )
                                }
                              />
                            }
                            label={
                              <Typography variant="body2">Incluir</Typography>
                            }
                          />
                        ) : null}
                      </Stack>
                      {info?.note ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="p"
                          sx={{ mb: group.columns.length ? 0.5 : 0 }}
                        >
                          {info.note}
                        </Typography>
                      ) : null}
                      {group.columns.length === 0 ? null : (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                            md: "repeat(4, minmax(0, 1fr))",
                          },
                          columnGap: 1,
                        }}
                      >
                        {group.columns.map((c) => (
                          <FormControlLabel
                            key={c.key}
                            control={
                              <Checkbox
                                size="small"
                                checked={!off && selectedSet.has(c.key)}
                                disabled={off || blockedByPdfLimit(c.key)}
                                onChange={() => toggleColumn(c.key)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {c.choiceLabel ?? c.label}
                              </Typography>
                            }
                          />
                        ))}
                      </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Step>
          </Stack>
        </Box>

        <Box
          sx={{
            p: 3,
            borderLeft: { md: 1 },
            borderTop: { xs: 1, md: 0 },
            borderColor: "divider",
            bgcolor: "action.hover",
            overflowY: { md: "auto" },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1.5, fontFamily: SANS }}
          >
            Tu archivo
          </Typography>
          <Stack component="dl" spacing={1.25} sx={{ m: 0 }}>
            {summaryRows.map((row) => (
              <Box key={row.label}>
                <Typography
                  component="dt"
                  variant="caption"
                  color="text.secondary"
                >
                  {row.label}
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, overflowWrap: "anywhere" }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
          {pending.length > 0 ? (
            <Stack spacing={0.75}>
              {pending.map((p) => (
                <Stack
                  key={p}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <PendingIcon
                    fontSize="small"
                    color="warning"
                    sx={{ mt: "1px" }}
                  />
                  <Typography variant="body2">{p}</Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <ReadyIcon fontSize="small" color="success" />
              <Typography variant="body2">Listo para exportar.</Typography>
            </Stack>
          )}
          {pdfTooLarge ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {pdfTooLarge}
            </Alert>
          ) : null}
          {error ? (
            <Alert
              severity={
                error.includes("Sin datos") || error.includes("Ninguna fila")
                  ? "info"
                  : "error"
              }
              sx={{ mt: 2 }}
            >
              {error}
            </Alert>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        {schema.formats.includes("pdf") ? (
          <Tooltip
            title={
              pdfTooLarge ??
              (pdfTrimsColumns
                ? `El PDF incluye hasta ${schema.maxPdfColumns} columnas por tabla`
                : "")
            }
          >
            <span>
              <DownloadButton
                format="pdf"
                label="Exportar PDF"
                variant={
                  schema.formats.includes("xlsx") ? "outlined" : "contained"
                }
                busyFormat={busyFormat}
                disabled={!canSubmit || !!pdfTooLarge}
                onClick={() => void handleExport("pdf")}
              />
            </span>
          </Tooltip>
        ) : null}
        {schema.formats.includes("xlsx") ? (
          <DownloadButton
            format="xlsx"
            label="Exportar Excel"
            variant="contained"
            busyFormat={busyFormat}
            disabled={!canSubmit}
            onClick={() => void handleExport("xlsx")}
          />
        ) : null}
      </DialogActions>
    </>
  );
};

const DownloadButton: React.FC<{
  format: ExportFormat;
  label: string;
  variant: "contained" | "outlined";
  busyFormat: ExportFormat | null;
  disabled: boolean;
  onClick: () => void;
}> = ({ format, label, variant, busyFormat, disabled, onClick }) => (
  <Button
    variant={variant}
    onClick={onClick}
    disabled={disabled}
    startIcon={
      busyFormat === format ? (
        <CircularProgress size={16} color="inherit" />
      ) : (
        <DownloadIcon />
      )
    }
  >
    {busyFormat === format ? "Generando…" : label}
  </Button>
);

const Step: React.FC<{
  n: number;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ n, title, action, children }) => (
  <Box component="section">
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: "text.primary",
          color: "background.paper",
          display: "grid",
          placeItems: "center",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {n}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, lineHeight: "24px", fontFamily: SANS }}
        >
          {title}
        </Typography>
      </Box>
      {action}
    </Stack>
    <Box sx={{ pl: { sm: 4.5 } }}>{children}</Box>
  </Box>
);

const ChoiceCard: React.FC<{
  selected: boolean;
  onClick: (() => void) | undefined;
  disabled?: boolean;
  multi?: boolean;
  title: string;
  description?: string | undefined;
}> = ({ selected, onClick, disabled, multi, title, description }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled || !onClick}
    aria-pressed={selected}
    sx={{
      display: "flex",
      alignItems: description ? "flex-start" : "center",
      justifyContent: "flex-start",
      gap: 1.25,
      p: 1.5,
      textAlign: "left",
      borderRadius: 1.5,
      border: 1,
      borderColor: selected ? "text.primary" : "divider",
      bgcolor: selected ? "action.selected" : "transparent",
      "&:hover": onClick ? { borderColor: "text.secondary" } : undefined,
      "&.Mui-disabled": { opacity: 1 },
    }}
  >
    {onClick ? (
      selected ? (
        multi ? (
          <CheckedIcon fontSize="small" sx={{ mt: description ? "1px" : 0 }} />
        ) : (
          <SelectedIcon fontSize="small" sx={{ mt: description ? "1px" : 0 }} />
        )
      ) : multi ? (
        <UncheckedIcon
          fontSize="small"
          sx={{ mt: description ? "1px" : 0, color: "text.secondary" }}
        />
      ) : (
        <UnselectedIcon
          fontSize="small"
          sx={{ mt: description ? "1px" : 0, color: "text.secondary" }}
        />
      )
    ) : null}
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, lineHeight: description ? undefined : "20px" }}
      >
        {title}
      </Typography>
      {description ? (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.25 }}
        >
          {description}
        </Typography>
      ) : null}
    </Box>
  </ButtonBase>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
    {children}
  </Typography>
);

export default ExportDialog;
